import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Container, CircularProgress, Alert, Grid, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
  Chip, Tooltip, IconButton, Snackbar,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getTestSessions, recordAiResults } from '../services/testSessionService';
import { topicService } from '../services/topicService';
import CustomAppBar from '../components/CustomAppBar';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { formatDateTime } from '../utils/dateUtils';

const TestHistoryPage = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedSession, setSelectedSession] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // AI paste state
    const [aiJsonText, setAiJsonText] = useState('');
    const [aiSubmitting, setAiSubmitting] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [aiSuccess, setAiSuccess] = useState(false);
    const [copySnackbar, setCopySnackbar] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchSessions = async () => {
            try {
                const data = await getTestSessions();
                setSessions(data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchSessions();
    }, []);

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h > 0 ? h + 'h ' : ''}${m}m ${s}s`;
    };

    const handleResume = (session) => {
        const formattedAnswers = session.answers || {};

        localStorage.setItem(`TEST_SESSION_${session.subjectId}`, JSON.stringify({
            sessionId: session.id,
            timeInSeconds: session.activeTimeSeconds,
            answers: formattedAnswers,
        }));

        navigate(`/subject/${session.subjectId}/test`);
    };

    const handleOpenResults = (session) => {
        setSelectedSession(session);
        setActiveTab(0);
        setAiJsonText('');
        setAiError(null);
        setAiSuccess(false);
    };

    const handleCloseResults = () => {
        setSelectedSession(null);
    };

    // Build copyable results text for the selected session
    const buildResultsText = (session) => {
        if (!session?.questions) return '';
        return session.questions.map((q, i) =>
            `--- Question ${i + 1} [ID: ${q.id}] ---\n` +
            `Q: ${q.questionText}\n` +
            `Your Answer: ${session.answers?.[q.id] || 'No answer provided'}\n` +
            `Correct Answer: ${q.answerText || 'No correct answer provided'}\n`
        ).join('\n');
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(buildResultsText(selectedSession)).then(() => {
            setCopySnackbar(true);
        });
    };

    // Handle AI JSON paste submission
    const handleAiSubmit = async () => {
        setAiError(null);
        setAiSuccess(false);

        let parsed;
        try {
            parsed = JSON.parse(aiJsonText.trim());
        } catch {
            setAiError('Invalid JSON. Please paste a valid JSON object.');
            return;
        }

        if (!parsed.results || !Array.isArray(parsed.results)) {
            setAiError('JSON must have a "results" array. E.g. { "results": [ { "questionId": 42, "correct": true } ] }');
            return;
        }

        setAiSubmitting(true);
        try {
            await recordAiResults(parsed.results);
            setAiSuccess(true);
            setAiJsonText('');
        } catch (err) {
            setAiError(err.message);
        } finally {
            setAiSubmitting(false);
        }
    };

    return (
        <CustomAppBar>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <HistoryIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Typography variant="h4" fontWeight="bold">Test History</Typography>
                </Box>

                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : sessions.length === 0 ? (
                    <Alert severity="info">You haven't taken any tests yet!</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {sessions.map(session => (
                            <Grid item xs={12} md={6} lg={4} key={session.id}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        borderLeft: `6px solid ${session.isCompleted ? '#4caf50' : '#ff9800'}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                    }}
                                >
                                    <Typography variant="h6" gutterBottom fontWeight="medium" color="primary.main">
                                        {session.subjectName}
                                    </Typography>

                                    <Box sx={{ mb: 2, flexGrow: 1 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            <strong>Last Active:</strong> {formatDateTime(session.updatedAt)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            <strong>Duration:</strong> {formatTime(session.activeTimeSeconds)}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom color="text.secondary">
                                            <strong>Questions:</strong> {session.questions?.length ?? '—'}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: session.isCompleted ? 'success.main' : 'warning.main',
                                            fontWeight: 'bold',
                                            mt: 1,
                                        }}>
                                            {session.isCompleted ? 'Completed' : 'In Progress'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #eee' }}>
                                        {session.isCompleted ? (
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                fullWidth
                                                startIcon={<AssessmentIcon />}
                                                onClick={() => handleOpenResults(session)}
                                            >
                                                View Results
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                onClick={() => handleResume(session)}
                                            >
                                                Resume Test
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            {/* ── Results Modal ─────────────────────────────────────────────── */}
            <Dialog
                open={Boolean(selectedSession)}
                onClose={handleCloseResults}
                maxWidth="md"
                fullWidth
            >
                {selectedSession && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                            <Typography variant="h6" fontWeight="bold">
                                {selectedSession.subjectName} — Results
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                Duration: {formatTime(selectedSession.activeTimeSeconds)}
                            </Typography>
                        </DialogTitle>

                        <Box sx={{ px: 3 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tab label="Q&A Results" icon={<AssessmentIcon />} iconPosition="start" />
                                <Tab label="Paste AI Grades" icon={<AutoAwesomeIcon />} iconPosition="start" />
                            </Tabs>
                        </Box>

                        <DialogContent dividers sx={{ pt: 2 }}>
                            {/* ── Tab 0: Q&A Results ─────────────────────────── */}
                            {activeTab === 0 && (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Copy this block and paste it into an AI. Ask it to evaluate your answers and output:
                                        <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: '0.73rem', whiteSpace: 'pre-wrap' }}>
                                            {`{ "results": [ { "questionId": <id>, "correct": true|false } ] }`}
                                        </Box>
                                        Then switch to the <strong>"Paste AI Grades"</strong> tab.
                                    </Alert>

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                        <Tooltip title="Copy to clipboard">
                                            <IconButton onClick={handleCopy} color="primary" size="small">
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Box sx={{
                                        bgcolor: '#1e1e1e',
                                        color: '#d4d4d4',
                                        p: 3,
                                        borderRadius: 1,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        maxHeight: '50vh',
                                        overflowY: 'auto',
                                    }}>
                                        {selectedSession.questions?.map((q, i) => {
                                            const userAns = selectedSession.answers?.[q.id] || 'No answer provided';
                                            return (
                                                <Box key={q.id} sx={{ mb: 3 }}>
                                                    {`--- Question ${i + 1} [ID: ${q.id}] ---\n`}
                                                    {`Q: ${q.questionText}\n`}
                                                    {`Your Answer: ${userAns}\n`}
                                                    {`Correct Answer: ${q.answerText || 'No correct answer provided'}\n`}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </>
                            )}

                            {/* ── Tab 1: Paste AI Grades ─────────────────────── */}
                            {activeTab === 1 && (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Paste the JSON from your AI evaluation below. The grades will be saved as
                                        official attempt records for each question.
                                    </Alert>

                                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Expected JSON format:
                                        </Typography>
                                        <Box component="pre" sx={{ m: 0, fontSize: '0.78rem', fontFamily: 'monospace', color: '#1e293b' }}>
{`{
  "results": [
    { "questionId": 42, "correct": true },
    { "questionId": 43, "correct": false }
  ]
}`}
                                        </Box>
                                    </Paper>

                                    {aiError && <Alert severity="error" sx={{ mb: 2 }}>{aiError}</Alert>}
                                    {aiSuccess && (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            ✅ AI results recorded successfully! Attempt history updated for each question.
                                        </Alert>
                                    )}

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        placeholder='Paste AI JSON here...'
                                        value={aiJsonText}
                                        onChange={(e) => {
                                            setAiJsonText(e.target.value);
                                            setAiError(null);
                                            setAiSuccess(false);
                                        }}
                                        variant="outlined"
                                        sx={{ fontFamily: 'monospace' }}
                                        inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                                    />

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        sx={{ mt: 2 }}
                                        disabled={aiSubmitting || !aiJsonText.trim()}
                                        onClick={handleAiSubmit}
                                        startIcon={aiSubmitting ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                                    >
                                        {aiSubmitting ? 'Recording…' : 'Record AI Grades'}
                                    </Button>
                                </Box>
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleCloseResults} color="primary">Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar
                open={copySnackbar}
                autoHideDuration={2500}
                onClose={() => setCopySnackbar(false)}
                message="Results copied to clipboard!"
            />
        </CustomAppBar>
    );
};

export default TestHistoryPage;
